import { Link } from 'react-router-dom';
import { Car, Gauge, Calendar, ArrowRight, ArrowLeft } from 'lucide-react';
import { imgSrc } from '@/api/auction';
import { at, sourceLabel, statusLabel, vatLabel } from '@/i18n/auctionI18n';

const STATUS_STYLE = {
  'ready-for-auction': 'bg-emerald-100 text-emerald-800',
  'in-yard': 'bg-sky-100 text-sky-800',
  'under-inspection': 'bg-amber-100 text-amber-800',
  'documentation-pending': 'bg-amber-100 text-amber-800',
  'sold': 'bg-neutral-200 text-neutral-600',
  'released': 'bg-neutral-200 text-neutral-600',
  'unavailable': 'bg-red-100 text-red-700',
};

export function LotCard({ v, lang = 'en' }) {
  const isAr = lang === 'ar';
  const base = isAr ? '/ar/auction' : '/auction';
  const img = v.images && v.images.length ? imgSrc(v.images[0]) : null;
  const Arrow = isAr ? ArrowLeft : ArrowRight;
  return (
    <Link to={`${base}/lot/${v.id}`} data-testid={`lot-card-${v.id}`}
      className="group block rounded-2xl overflow-hidden border border-black/10 bg-white hover:border-[#EE5A01]/50 hover:shadow-lg transition-all">
      <div className="aspect-[16/10] bg-[#F2F2EF] relative overflow-hidden">
        {img ? (
          <img src={img} alt={`${v.make} ${v.model}`} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-[#C4C4BE]"><Car className="w-12 h-12" /></div>
        )}
        <span className={`absolute top-3 ${isAr ? 'right-3' : 'left-3'} bg-[#121212]/85 text-white font-mono text-[10px] px-2 py-1 rounded`}>{v.lot_no}</span>
        {v.status_label && (
          <span className={`absolute top-3 ${isAr ? 'left-3' : 'right-3'} text-[10px] font-heading font-bold uppercase tracking-wide px-2 py-1 rounded ${STATUS_STYLE[v.availability_status] || 'bg-neutral-200 text-neutral-700'}`}>{statusLabel(lang, v.availability_status, v.status_label)}</span>
        )}
      </div>
      <div className="p-4 sm:p-5">
        <h3 className="font-heading font-bold text-base text-[#121212] leading-snug">{v.year} {v.make} {v.model}</h3>
        <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1.5 text-xs text-[#666666] font-body">
          <span className="inline-flex items-center gap-1.5"><Gauge className="w-3.5 h-3.5 text-[#EE5A01]" />{Number(v.mileage_km).toLocaleString()} {at(lang, 'km')}</span>
          {v.transmission && <span className="inline-flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5 text-[#EE5A01]" />{v.transmission}</span>}
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          {v.source_label && <span className="font-body text-[10px] text-[#555] bg-[#F5F5F3] px-2 py-1 rounded">{sourceLabel(lang, v.source, v.source_label)}</span>}
          {v.vat_label && <span className="font-body text-[10px] text-[#555] bg-[#F5F5F3] px-2 py-1 rounded">{vatLabel(lang, v.vat_treatment, v.vat_label)}</span>}
        </div>
        <span className="mt-4 inline-flex items-center gap-1 font-heading font-bold text-xs text-[#EE5A01] group-hover:underline">{at(lang, 'viewDetails')} <Arrow className="w-3.5 h-3.5" /></span>
      </div>
    </Link>
  );
}
